import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Eye, MousePointer, TrendingUp, ExternalLink } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface GscQueriesTableProps {
  days: number
}

export default function GscQueriesTable({ days }: GscQueriesTableProps) {
  const [view, setView] = useState<'queries' | 'pages'>('queries')

  const queriesQuery = useQuery({
    queryKey: ['gsc-queries', days],
    queryFn: () => seoAnalytics.getGscQueries(days, 50),
    enabled: view === 'queries',
  })

  const pagesQuery = useQuery({
    queryKey: ['gsc-pages', days],
    queryFn: () => seoAnalytics.getGscPages(days, 50),
    enabled: view === 'pages',
  })

  const isLoading = view === 'queries' ? queriesQuery.isLoading : pagesQuery.isLoading
  const data = view === 'queries' ? queriesQuery.data?.data : pagesQuery.data?.data
  const items = data || []

  // Truncate for display
  const truncate = (str: string, maxLength: number = 60) => {
    if (!str) return '-'
    if (str.length <= maxLength) return str
    return str.substring(0, maxLength) + '...'
  }

  // Get position color
  const getPositionColor = (pos: number) => {
    if (pos <= 3) return 'text-green-600 dark:text-green-400'
    if (pos <= 10) return 'text-blue-600 dark:text-blue-400'
    if (pos <= 20) return 'text-amber-600 dark:text-amber-400'
    return 'text-slate-600 dark:text-slate-400'
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30">
            <Search className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {view === 'queries' ? 'Top Keywords' : 'Top Paginas'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Rendimiento en busquedas de Google
            </p>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setView('queries')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'queries'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Keywords
          </button>
          <button
            onClick={() => setView('pages')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'pages'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Paginas
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay datos de Search Console</p>
            <p className="text-sm mt-1">Configura GSC para ver datos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {view === 'queries' ? 'Keyword' : 'Pagina'}
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <Eye className="w-3 h-3" />
                      Impresiones
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <MousePointer className="w-3 h-3" />
                      Clics
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    CTR
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Posicion
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.slice(0, 20).map((item: any, idx: number) => (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      {view === 'queries' ? (
                        <span
                          className="text-sm text-slate-700 dark:text-slate-300"
                          title={item.query}
                        >
                          {truncate(item.query)}
                        </span>
                      ) : (
                        <a
                          href={item.page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                          title={item.page_url}
                        >
                          {truncate(item.page_url?.replace('https://www.jvs-informatica.com', ''))}
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {item.impressions?.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {item.clicks?.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        item.ctr > 5 ? 'text-green-600 dark:text-green-400' :
                        item.ctr > 2 ? 'text-amber-600 dark:text-amber-400' :
                        'text-slate-600 dark:text-slate-400'
                      }`}>
                        {item.ctr}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${getPositionColor(item.avg_position)}`}>
                        {item.avg_position}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Show count */}
        {items.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-500">
            Mostrando {Math.min(20, items.length)} de {items.length} {view === 'queries' ? 'keywords' : 'paginas'}
          </div>
        )}
      </div>
    </div>
  )
}
